import { apiFetch, apiFetchWithRetry } from '@/lib/apiFetch';
import { handleUnauthorized } from './UserManagmentServices';
import type { ProfileUIResponse } from '@/types/teacherProfile';
import type { PageResponse } from './inquiryService';

const UI_BASE = '/api/ui/v1/teacher/profile';

// ─── Helper ─────────────────────────────────────────────────────

async function authFetch<T>(path: string, accessToken: string, options?: RequestInit): Promise<T> {
  const response = await apiFetch(path, {
    ...options,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      ...options?.headers,
    },
  });
  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = errorText || `Request failed: ${response.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.message || errorJson.error || errorMessage;
    } catch { /* not JSON — use raw text */ }
    throw new Error(errorMessage);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

// ─── Profile GET ────────────────────────────────────────────────

export const getTeacherProfile = async (accessToken: string): Promise<ProfileUIResponse | null> => {
  const response = await apiFetch(UI_BASE, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (response.status === 404 || response.status === 500) {
    console.warn(`GET ${UI_BASE} returned ${response.status} — treating as no profile`);
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch profile: ${response.status}`);
  }
  return response.json();
};

// ─── Section saves ──────────────────────────────────────────────

export const saveBasicInfo = async (data: {
  handle: string;
  teachingModes: string[];
  subjects: { subjectId: string; topicIds: string[] }[];
  placeId: string;
  locationDisplayName: string;
  latitude: number;
  longitude: number;
  teachingAudience?: string[];
  curriculumSystems?: string[];
  instructionLanguages?: string[];
  otherInstructionLanguage?: string;
  subjectExpertise?: Array<{ subjectId: string; depthLevel: string }>;
}, accessToken: string): Promise<ProfileUIResponse> => {
  return authFetch(`${UI_BASE}/sections/basic_info`, accessToken, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

export const saveDescription = async (data: {
  professionalBio: string;
  teachingApproach: string;
  examSuccessStories?: Array<{
    studentCount: number;
    achievement: string;
    threshold?: string;
    subjectExamId?: string;
    timeframe: string;
  }>;
}, accessToken: string): Promise<ProfileUIResponse> => {
  return authFetch(`${UI_BASE}/sections/description`, accessToken, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

export const saveEducation = async (data: {
  highestQualification?: string;
  otherHighestQualification?: string;
  highestQualificationInstitution?: string;
  teachingQualification?: string;
  otherTeachingQualification?: string;
  yearsTeachingExperience?: string;
  formerSchoolTeacher?: boolean;
  formerInstitutionName?: string;
  formerInstitutionYears?: number;
}, accessToken: string): Promise<ProfileUIResponse> => {
  return authFetch(`${UI_BASE}/sections/education`, accessToken, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

// ─── Profile actions ────────────────────────────────────────────

export const submitTeacherProfile = async (accessToken: string): Promise<ProfileUIResponse> => {
  return authFetch(`${UI_BASE}/submit`, accessToken, { method: 'POST' });
};

export const discardDraft = async (accessToken: string): Promise<void> => {
  return authFetch(`${UI_BASE}/draft`, accessToken, { method: 'DELETE' });
};

export const withdrawDraft = async (accessToken: string): Promise<ProfileUIResponse> => {
  return authFetch(`${UI_BASE}/withdraw`, accessToken, { method: 'POST' });
};

// ─── Media — profile image ──────────────────────────────────────

export const uploadProfileImage = async (imageFile: File, accessToken: string): Promise<{ profileImageUrl: string }> => {
  const formData = new FormData();
  formData.append('file', imageFile);

  const response = await apiFetch(`${UI_BASE}/media/profile-image/upload`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
    body: formData,
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to upload profile image: ${response.status} - ${errorText}`);
  }
  return response.json();
};

export const deleteProfileImage = async (accessToken: string): Promise<void> => {
  return authFetch(`${UI_BASE}/media/profile-image`, accessToken, { method: 'DELETE' });
};

// ─── Handle availability check ──────────────────────────────────

export const checkHandleAvailability = async (
  handle: string,
  accessToken: string,
): Promise<{ available: boolean }> => {
  return authFetch(
    `${UI_BASE}/handle-available?handle=${encodeURIComponent(handle)}`,
    accessToken,
  );
};

// ─── Coin transactions ────────────────────────────────────────────

export interface TeacherCoinTransaction {
  createdAt: string;
  reason: 'REGISTRATION_GRANT' | 'INQUIRY_CHARGE';
  amount: number;
  balanceAfter: number;
}

export async function fetchTeacherCoinTransactions(accessToken: string): Promise<TeacherCoinTransaction[]> {
  const response = await apiFetchWithRetry('/api/v1/teacher/coin-transactions', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch coin transactions: ${response.status}`);
  }
  return response.json();
}

// --- Coin spend table (paginated, filterable) ---

export interface TeacherCoinSpendRow {
  id: string;
  threadId: string | null;
  counterpartName: string | null;
  createdAt: string;
  amount: number;
  balanceAfter: number;
  reason: string;
}

export interface RegistrationGrantSummary {
  amount: number;
  createdAt: string;
}

/** Raw shape returned by the backend — `studentName` is mapped to `counterpartName` before this leaves the service layer. */
interface TeacherCoinSpendRowResponse {
  id: string;
  threadId: string | null;
  studentName: string | null;
  createdAt: string;
  amount: number;
  balanceAfter: number;
  reason: string;
}

/**
 * Paginated, filterable coin-spend history for the current teacher.
 *
 * `dateFrom`/`dateTo` are accepted as `Date` objects and serialized to full ISO
 * instant strings here via `.toISOString()` — callers (page-level components)
 * are responsible for constructing an end-of-day `Date` for `dateTo` if they
 * want the inclusive backend `<=` to cover the whole selected day. Per ADR 085,
 * the query param is OMITTED entirely (never sent as an empty string) when the
 * caller passes `undefined`, since the backend's `Instant` binding cannot
 * parse `""`.
 */
export async function fetchTeacherCoinSpends(
  accessToken: string,
  params: { page: number; size: number; name?: string; dateFrom?: Date; dateTo?: Date }
): Promise<PageResponse<TeacherCoinSpendRow>> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('size', String(params.size));
  if (params.name) qs.set('name', params.name);
  if (params.dateFrom) qs.set('dateFrom', params.dateFrom.toISOString());
  if (params.dateTo) qs.set('dateTo', params.dateTo.toISOString());

  const response = await apiFetchWithRetry(`/api/v1/teacher/coin-transactions?${qs}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch coin spends: ${response.status}`);
  }
  const page: PageResponse<TeacherCoinSpendRowResponse> = await response.json();
  return {
    ...page,
    content: page.content.map(({ studentName, ...rest }) => ({
      ...rest,
      counterpartName: studentName,
    })),
  };
}

export async function fetchTeacherRegistrationGrant(accessToken: string): Promise<RegistrationGrantSummary | null> {
  const response = await apiFetchWithRetry('/api/v1/teacher/coin-transactions/registration-grant', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (response.status === 204) return null;
  if (!response.ok) {
    throw new Error(`Failed to fetch registration grant: ${response.status}`);
  }
  return response.json();
}

export interface TeacherCoinSummary {
  balance: number | null;
  /** Effective (tier-discount-aware) cost of a reply — what the teacher will actually be charged. */
  chargeAmount: number;
  /** Undiscounted standard rate, before any subscription-tier discount is applied. */
  standardChargeAmount: number;
  /** Teacher's tier discount percentage (0 for Silver/no subscription, 10 for Gold, 15 for Platinum). */
  coinDiscountPct: number;
}

export async function fetchTeacherCoinSummary(accessToken: string): Promise<TeacherCoinSummary> {
  const response = await apiFetchWithRetry('/api/v1/teacher/coins/summary', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
  if (response.status === 401) {
    handleUnauthorized();
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch coin summary: ${response.status}`);
  }
  return response.json();
}

// ─── Public profile (unchanged — no auth required) ──────────────

export const getPublicTeacherProfileById = async (teacherId: string) => {
  const response = await apiFetch(`/api/v1/marketplace/teachers/${teacherId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Failed to fetch teacher profile: ${response.status}`);
  return response.json();
};

export const getPublicOfferingById = async (offeringId: string) => {
  const response = await apiFetch(`/api/v1/marketplace/teachers/offerings/${offeringId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Failed to fetch offering: ${response.status}`);
  return response.json();
};

export const getPublicTeacherOfferingsById = async (teacherId: string) => {
  const response = await apiFetch(`/api/v1/marketplace/teachers/${teacherId}/offerings`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (response.status === 404) return [];
  if (!response.ok) throw new Error(`Failed to fetch teacher offerings: ${response.status}`);
  const text = await response.text();
  const data = text ? JSON.parse(text) : [];
  return Array.isArray(data) ? data : data?.content ?? [];
};

// ─── Legacy aliases ──────────────────────────────────────────────
/** @deprecated Use saveBasicInfo */
export const updateTeacherBasicInfo = saveBasicInfo;
/** @deprecated Use saveDescription */
export const updateDescription = saveDescription;
